import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { useProperty } from "../lib/property-context";
import { useEffectiveRole } from "../lib/view-as-context";
import { LeaseDocsManager } from "../components/LeaseDocsManager";
import { LeaseRentManager } from "../components/LeaseRentManager";
import { AddTenantDialog } from "../components/AddTenantDialog";
import { StartLeaseDialog } from "../components/StartLeaseDialog";
import { TenancyDocsViewer } from "../components/TenancyDocsViewer";
import {
  UnitsManagerDialog,
  UnitThumbnail,
} from "../components/UnitsManagerDialog";
import { AppHeader } from "../components/AppHeader";

type Role = "landlord" | "tenant" | null | undefined;

interface TenancyOnLease {
  _id: string;
  isActive: boolean;
  startDate: string;
  endDate: string | null;
  tenant: {
    _id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  } | null;
}

interface PendingInvite {
  _id: string;
  email: string | null;
  phoneNumber: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: number;
}

interface Lease {
  _id: string;
  isActive: boolean;
  startDate: string;
  endDate: string | null;
  monthlyRentCents: number;
  notes: string | null;
  unit: {
    _id: string;
    label: string;
    thumbnailUrl: string | null;
  } | null;
  tenancies: TenancyOnLease[];
  pendingInvites: PendingInvite[];
}

export default function Tenants() {
  const property = useProperty();
  const realRole = useQuery(
    anyApi.memberships.getMyRoleOnProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as Role;
  const role = useEffectiveRole(realRole);
  const leases = useQuery(
    anyApi.leases.listForProperty,
    property ? { propertySlug: property.slug } : "skip",
  ) as Lease[] | undefined;

  const [startLeaseOpen, setStartLeaseOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);

  if (role === "tenant") {
    return <Navigate to={`/${property?.slug}/dashboard`} replace />;
  }

  const active = (leases ?? []).filter((l) => l.isActive);
  const past = (leases ?? []).filter((l) => !l.isActive);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Tenants
            </h1>
            <p className="mt-2 text-slate-600">
              Grouped by lease. Each lease owns its own contract document, so
              new tenants on a fresh lease never see the previous tenants'
              paperwork.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setUnitsOpen(true)}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Units
            </button>
            <button
              type="button"
              onClick={() => setStartLeaseOpen(true)}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 transition-colors"
            >
              Start new lease
            </button>
          </div>
        </div>

        {leases === undefined ? (
          <SkeletonList />
        ) : leases.length === 0 ? (
          <Empty />
        ) : (
          <>
            <Group title="Active leases">
              {active.length === 0 ? (
                <Hint>No active leases right now.</Hint>
              ) : (
                <ul className="space-y-4">
                  {active.map((l) => (
                    <LeaseCard key={l._id} lease={l} />
                  ))}
                </ul>
              )}
            </Group>
            {past.length > 0 && (
              <Group title="Past leases">
                <ul className="space-y-4">
                  {past.map((l) => (
                    <LeaseCard key={l._id} lease={l} />
                  ))}
                </ul>
              </Group>
            )}
          </>
        )}
      </main>

      {property && (
        <StartLeaseDialog
          open={startLeaseOpen}
          onClose={() => setStartLeaseOpen(false)}
          propertySlug={property.slug}
        />
      )}
      {property && (
        <UnitsManagerDialog
          open={unitsOpen}
          onClose={() => setUnitsOpen(false)}
          propertySlug={property.slug}
        />
      )}
    </div>
  );
}

function Group({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function LeaseCard({ lease }: { lease: Lease }) {
  const closeLease = useMutation(anyApi.leases.close);
  const reopenLease = useMutation(anyApi.leases.reopen);
  const deactivate = useMutation(anyApi.tenancies.deactivate);
  const reactivate = useMutation(anyApi.tenancies.reactivate);
  const removeTenancy = useMutation(anyApi.tenancies.remove);
  const revokeInvite = useMutation(anyApi.tenancies.revokePendingInvite);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addTenantOpen, setAddTenantOpen] = useState(false);

  const handleCloseLease = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const endDate = prompt(
      `End date for this lease?\nFormat YYYY-MM-DD; defaults to today. Any remaining active tenants will end on the same date.`,
      today,
    );
    if (endDate === null) return;
    setBusy(true);
    setError(null);
    try {
      await closeLease({
        leaseId: lease._id,
        endDate: endDate.trim() || today,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    setBusy(true);
    setError(null);
    try {
      await reopenLease({ leaseId: lease._id });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivateTenant = async (tenancyId: string, name: string) => {
    const today = new Date().toISOString().slice(0, 10);
    const endDate = prompt(
      `End date for ${name}'s tenancy on this lease?\nFormat YYYY-MM-DD; defaults to today.`,
      today,
    );
    if (endDate === null) return;
    setBusy(true);
    setError(null);
    try {
      await deactivate({
        tenancyId,
        endDate: endDate.trim() || today,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleReactivateTenant = async (tenancyId: string) => {
    setBusy(true);
    setError(null);
    try {
      await reactivate({ tenancyId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteTenant = async (tenancyId: string, name: string) => {
    if (
      !confirm(
        `Delete ${name}'s tenancy on this lease entirely?\n\n` +
          `This removes the row from the ledger and any availability windows ` +
          `they set. Personal documents they uploaded stay (delete those ` +
          `from the per-tenant Show personal docs section if needed).`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await removeTenancy({ tenancyId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-xl border border-slate-200 bg-white">
      <header className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {lease.unit && (
            <UnitThumbnail
              url={lease.unit.thumbnailUrl}
              label={lease.unit.label}
              size="sm"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-slate-900">
                {lease.unit?.label ?? "Unknown unit"}
              </p>
              {lease.isActive ? (
                <Badge tone="emerald">Active lease</Badge>
              ) : (
                <Badge tone="slate">Closed</Badge>
              )}
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              {formatRange(lease.startDate, lease.endDate)}
              {lease.monthlyRentCents > 0 && (
                <>
                  {" · "}
                  ${(lease.monthlyRentCents / 100).toFixed(0)}/mo
                </>
              )}
            </p>
          </div>
        </div>
        <div className="shrink-0 self-center flex items-center gap-1">
          {lease.isActive && (
            <button
              type="button"
              onClick={() => setAddTenantOpen(true)}
              disabled={busy}
              className="text-xs font-medium text-slate-700 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
            >
              Add tenant
            </button>
          )}
          {lease.isActive ? (
            <button
              type="button"
              onClick={handleCloseLease}
              disabled={busy}
              className="text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
            >
              {busy ? "…" : "Close lease"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleReopen}
              disabled={busy}
              className="text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
            >
              {busy ? "…" : "Reopen"}
            </button>
          )}
        </div>
      </header>
      <div className="px-5 py-3">
        {lease.tenancies.length === 0 && lease.pendingInvites.length === 0 ? (
          <p className="text-sm text-slate-500">No tenants on this lease.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lease.tenancies.map((t) => {
              const name = t.tenant
                ? t.tenant.firstName
                  ? t.tenant.lastName
                    ? `${t.tenant.firstName} ${t.tenant.lastName}`
                    : t.tenant.firstName
                  : t.tenant.email
                : "Unknown user";
              return (
                <li
                  key={t._id}
                  className="py-3 flex items-start justify-between gap-4 first:pt-0"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-900">
                        {name}
                      </p>
                      {t.isActive ? (
                        <Badge tone="emerald">Active</Badge>
                      ) : (
                        <Badge tone="slate">Inactive</Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {t.tenant?.email}
                      {" · "}
                      {formatRange(t.startDate, t.endDate)}
                    </p>
                    <TenancyDocsViewer tenancyId={t._id} />
                  </div>
                  <div className="shrink-0 self-center flex items-center gap-1">
                    {t.isActive ? (
                      <button
                        type="button"
                        onClick={() => handleDeactivateTenant(t._id, name)}
                        disabled={busy}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                      >
                        Make inactive
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleReactivateTenant(t._id)}
                        disabled={busy}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                      >
                        Reactivate
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDeleteTenant(t._id, name)}
                      disabled={busy}
                      className="text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {lease.pendingInvites.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <h4 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
              Pending invites
            </h4>
            <ul className="mt-2 divide-y divide-slate-100">
              {lease.pendingInvites.map((p) => (
                <li
                  key={p._id}
                  className="py-2 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-slate-900">
                        {inviteName(p)}
                      </p>
                      <Badge tone="amber">Pending</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {inviteIdentifier(p)} · invited {timeAgo(p.createdAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (
                        !confirm(`Revoke invite to ${inviteIdentifier(p)}?`)
                      )
                        return;
                      try {
                        await revokeInvite({ inviteId: p._id });
                      } catch (err) {
                        setError(
                          err instanceof Error ? err.message : String(err),
                        );
                      }
                    }}
                    className="shrink-0 self-center text-xs font-medium text-slate-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-slate-100"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        <LeaseDocsManager leaseId={lease._id} />
        <LeaseRentManager leaseId={lease._id} />
      </div>

      <AddTenantDialog
        open={addTenantOpen}
        onClose={() => setAddTenantOpen(false)}
        leaseId={lease._id}
        unitLabel={lease.unit?.label}
      />
    </li>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "emerald" | "slate" | "amber";
  children: React.ReactNode;
}) {
  const styles =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
      : tone === "amber"
        ? "bg-amber-50 text-amber-700 ring-amber-200"
        : "bg-slate-100 text-slate-500 ring-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles}`}
    >
      {children}
    </span>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-slate-500">{children}</p>;
}

function Empty() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-6 py-16 text-center">
      <p className="text-sm font-medium text-slate-900">No leases yet.</p>
      <p className="mt-1 text-sm text-slate-500">
        Use{" "}
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono text-slate-800">
          npx convex run users:grantTenancy
        </code>{" "}
        to attach a signed-in user to a unit (auto-creates the lease). Or
        run{" "}
        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono text-slate-800">
          npx convex run leases:startNew
        </code>{" "}
        to spin up an empty lease first. Inline UI for both lands next.
      </p>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="rounded-xl border border-slate-200 bg-white px-6 py-4 animate-pulse space-y-2"
        >
          <div className="h-4 w-1/3 rounded bg-slate-100" />
          <div className="h-3 w-1/2 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

function inviteName(invite: PendingInvite): string {
  if (invite.firstName) {
    return invite.lastName
      ? `${invite.firstName} ${invite.lastName.charAt(0)}.`
      : invite.firstName;
  }
  if (invite.email) return invite.email.split("@")[0];
  if (invite.phoneNumber) return invite.phoneNumber;
  return "tenant";
}

function inviteIdentifier(invite: PendingInvite): string {
  if (invite.email && invite.phoneNumber) {
    return `${invite.email} / ${invite.phoneNumber}`;
  }
  return invite.email ?? invite.phoneNumber ?? "";
}

function timeAgo(ts: number): string {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatRange(start: string, end: string | null): string {
  const s = new Date(`${start}T00:00:00`);
  const sFmt = s.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (!end) return `Since ${sFmt}`;
  const e = new Date(`${end}T00:00:00`);
  const eFmt = e.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${sFmt} – ${eFmt}`;
}

